import { getApps, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  onAuthStateChanged,
  RecaptchaVerifier,
  setPersistence,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { app as defaultApp, isFirebaseConfigured } from "../lib/firebase";

const CUSTOMER_APP_NAME = "quickdrop-website-customer";
const customerApp = isFirebaseConfigured && defaultApp
  ? getApps().find((candidate) => candidate.name === CUSTOMER_APP_NAME)
    || initializeApp(defaultApp.options, CUSTOMER_APP_NAME)
  : null;

export const customerAuth = customerApp ? getAuth(customerApp) : null;
const persistenceReady = customerAuth
  ? setPersistence(customerAuth, browserLocalPersistence)
  : Promise.resolve();

let activeRecaptchaVerifier = null;

const customerAuthError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

export function normalizeIndianPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!/^[6-9]\d{9}$/.test(digits)) {
    throw customerAuthError(
      "auth/invalid-phone-number",
      "Enter a valid 10-digit Indian mobile number."
    );
  }
  return `+91${digits}`;
}

export function friendlyCustomerAuthError(error) {
  const messages = {
    "auth/invalid-phone-number": "Enter a valid 10-digit Indian mobile number.",
    "auth/invalid-verification-code": "The OTP is incorrect. Please check it and try again.",
    "auth/code-expired": "This OTP has expired. Please request a new one.",
    "auth/missing-verification-code": "Enter the 6-digit OTP sent to your mobile.",
    "auth/quota-exceeded": "OTP requests are temporarily unavailable. Please try again later.",
    "auth/too-many-requests": "Too many attempts. Please wait before trying again.",
    "auth/captcha-check-failed": "Security verification failed. Please try sending the OTP again.",
    "auth/operation-not-allowed": "Phone login is not available right now.",
    "auth/unauthorized-domain": "Phone login is not enabled for this website domain yet.",
    "auth/network-request-failed": "Check your internet connection and try again.",
    "auth/not-configured": "Customer login is not configured.",
  };
  return messages[error?.code] || "Unable to complete phone login. Please try again.";
}

export function clearCustomerRecaptcha() {
  if (!activeRecaptchaVerifier) return;
  try {
    activeRecaptchaVerifier.clear();
  } catch (_) {
    // The widget may already have been cleared by Firebase.
  }
  activeRecaptchaVerifier = null;
}

export async function sendCustomerOtp(phoneNumber, containerId) {
  if (!customerAuth) {
    throw customerAuthError("auth/not-configured", "Customer login is not configured.");
  }

  const normalizedPhone = normalizeIndianPhone(phoneNumber);
  await persistenceReady;
  clearCustomerRecaptcha();

  const verifier = new RecaptchaVerifier(customerAuth, containerId, {
    size: "invisible",
  });
  activeRecaptchaVerifier = verifier;

  try {
    await verifier.render();
    return await signInWithPhoneNumber(customerAuth, normalizedPhone, verifier);
  } catch (error) {
    clearCustomerRecaptcha();
    throw error;
  }
}

export async function verifyCustomerOtp(confirmationResult, otp) {
  const code = String(otp || "").trim();
  if (!/^\d{6}$/.test(code)) {
    throw customerAuthError(
      "auth/missing-verification-code",
      "Enter the 6-digit OTP sent to your mobile."
    );
  }
  if (!confirmationResult?.confirm) {
    throw customerAuthError("auth/code-expired", "Request a new OTP and try again.");
  }
  return confirmationResult.confirm(code);
}

export function subscribeCustomerAuth(listener) {
  if (!customerAuth) {
    listener(null);
    return () => {};
  }
  return onAuthStateChanged(customerAuth, listener);
}

export async function logoutCustomer() {
  if (customerAuth) await signOut(customerAuth);
}
